<template>
    <Dialog v-model:open="open">
        <DialogContent class="sm:max-w-[425px]">
            <div v-if="loading" class="h-full flex items-center justify-center text-xs space-x-2">
                <Loader class="w-4 h-4 animate-spin" />
                <span>Loading...</span>
            </div>
            <template v-else>
                <DialogHeader>
                    <DialogTitle>Edit Vocus NBN Server</DialogTitle>
                </DialogHeader>
                <ScrollArea class="h-[500px] rounded-md p-3">
                    <div class="space-y-3">
                        <FormField v-slot="{ componentField }" name="title">
                            <FormItem>
                                <FormLabel>Title (Required)</FormLabel>
                                <FormControl>
                                    <Input v-bind="componentField" placeholder="Title" />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        </FormField>
                        <FormField v-slot="{ componentField }" name="account_username">
                            <FormItem>
                                <FormLabel>Account Username (Required)</FormLabel>
                                <FormControl>
                                    <Input v-bind="componentField" placeholder="Account Username" />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        </FormField>
                        <FormField v-slot="{ componentField }" name="account_password">
                            <FormItem>
                                <FormLabel>Account Password (Required)</FormLabel>
                                <FormControl>
                                    <Password
                                        v-bind="componentField"
                                        placeholder="Account Password"
                                        can-generate
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        </FormField>
                        <FormField v-slot="{ componentField }" name="auth_client_id">
                            <FormItem>
                                <FormLabel>Auth Client ID (Required)</FormLabel>
                                <FormControl>
                                    <Input v-bind="componentField" placeholder="Auth Client ID" />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        </FormField>
                        <FormField v-slot="{ componentField }" name="auth_client_secret">
                            <FormItem>
                                <FormLabel>Auth Client Secret (Required)</FormLabel>
                                <FormControl>
                                    <Input
                                        v-bind="componentField"
                                        placeholder="Auth Client Secret"
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        </FormField>
                        <FormField v-slot="{ componentField }" name="api_client_id">
                            <FormItem>
                                <FormLabel>API Client ID (Required)</FormLabel>
                                <FormControl>
                                    <Input v-bind="componentField" placeholder="API Client ID" />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        </FormField>
                        <FormField v-slot="{ componentField }" name="api_client_secret">
                            <FormItem>
                                <FormLabel>API Client Secret (Required)</FormLabel>
                                <FormControl>
                                    <Input
                                        v-bind="componentField"
                                        placeholder="API Client Secret"
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        </FormField>
                        <FormField v-slot="{ componentField }" name="wsm_cert_pass">
                            <FormItem>
                                <FormLabel>WSM Certificate Password (Required)</FormLabel>
                                <FormControl>
                                    <Password
                                        v-bind="componentField"
                                        placeholder="WSM Certificate Password"
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        </FormField>
                        <FormField v-slot="{ componentField }" name="wsm_access_key">
                            <FormItem>
                                <FormLabel>WSM Access Key (Required)</FormLabel>
                                <FormControl>
                                    <Input v-bind="componentField" placeholder="WSM Access Key" />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        </FormField>
                        <FormField v-slot="{ componentField }" name="proxy_host">
                            <FormItem>
                                <FormLabel>Proxy Host (Required)</FormLabel>
                                <FormControl>
                                    <Input v-bind="componentField" placeholder="Proxy Host" />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        </FormField>
                        <FormField v-slot="{ componentField }" name="proxy_port">
                            <FormItem>
                                <FormLabel>Proxy Port (Required)</FormLabel>
                                <FormControl>
                                    <Input
                                        type="number"
                                        v-bind="componentField"
                                        placeholder="Proxy Port"
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        </FormField>
                    </div>
                </ScrollArea>
                <DialogFooter>
                    <Button
                        type="button"
                        variant="secondary"
                        :disabled="isCreatePending"
                        @click="close"
                        >Cancel</Button
                    >
                    <Button
                        type="button"
                        :loading="isCreatePending"
                        :disabled="!meta.valid || !meta.dirty"
                        @click="submit"
                        >Save</Button
                    >
                </DialogFooter>
            </template>
        </DialogContent>
    </Dialog>
</template>

<script setup lang="ts">
import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import Password from '@/components/ui/input/Password.vue'
import ScrollArea from '@/components/ui/scroll-area/ScrollArea.vue'
import { vocusNBNGraphQLClient } from '@/composables/default-urql-client'
import { setFieldsError } from '@/lib/utils'
import { useUpdateVocusNBNServerMutation } from '@/queries/vocus-nbn'
import { toTypedSchema } from '@vee-validate/zod'
import gql from 'graphql-tag'
import { Loader } from 'lucide-vue-next'
import { useForm } from 'vee-validate'
import { computed, onMounted, ref, watch } from 'vue'
import { z } from 'zod'

const props = defineProps<{
    server: {
        id: string
    }
}>()

const open = defineModel()

const { mutate: updateServer, isPending: isCreatePending } = useUpdateVocusNBNServerMutation()

const loading = ref(false)

const schema = z.object({
    title: z.string().min(1, {
        message: "You can't leave this empty.",
    }),
    account_username: z.string().min(1, {
        message: "You can't leave this empty.",
    }),
    account_password: z.string().min(1, {
        message: "You can't leave this empty.",
    }),
    auth_client_id: z.string().min(1, {
        message: "You can't leave this empty.",
    }),
    auth_client_secret: z.string().min(1, {
        message: "You can't leave this empty.",
    }),
    api_client_id: z.string().min(1, {
        message: "You can't leave this empty.",
    }),
    api_client_secret: z.string().min(1, {
        message: "You can't leave this empty.",
    }),
    wsm_cert_pass: z.string().min(1, {
        message: "You can't leave this empty.",
    }),
    wsm_access_key: z.string().min(1, {
        message: "You can't leave this empty.",
    }),
    wsm_cert_file: z.any().nullish(),
    proxy_host: z.string().min(1, {
        message: "You can't leave this empty.",
    }),
    proxy_port: z.number().min(1, {
        message: "You can't leave this empty.",
    }),
})

const form = useForm({
    validationSchema: toTypedSchema(schema),
})

const meta = computed(() => form.meta.value)

const submit = form.handleSubmit(async (values) => {
    if (!meta.value.dirty || !meta.value.valid) return

    const payload = {
        ...values,
        id: props.server.id,
    }

    updateServer(payload, {
        onSuccess: () => {
            close()
        },
        onError: (error) => {
            console.log(error)
            setFieldsError(error, form.setFieldError)
        },
    })
})

const init = async () => {
    try {
        loading.value = true

        const { data: server } = await vocusNBNGraphQLClient.query(
            gql`
                query editVocusNBNServer($id: String!) {
                    server(id: $id) {
                        id
                        title
                        account_username
                        account_password
                        auth_client_id
                        auth_client_secret
                        api_client_id
                        api_client_secret
                        wsm_cert_pass
                        wsm_access_key
                        proxy_host
                        proxy_port
                    }
                }
            `,
            {
                id: props.server?.id,
            }
        )

        const data = server?.server

        if (data) {
            form.resetForm({
                values: {
                    title: data.title,
                    account_username: data.account_username,
                    account_password: data.account_password,
                    auth_client_id: data.auth_client_id,
                    auth_client_secret: data.auth_client_secret,
                    api_client_id: data.api_client_id,
                    api_client_secret: data.api_client_secret,
                    wsm_cert_pass: data.wsm_cert_pass,
                    wsm_access_key: data.wsm_access_key,
                    proxy_host: data.proxy_host,
                    proxy_port: data.proxy_port,
                    id: props.server?.id,
                },
            })
        }
    } catch (e) {
        console.log(e)
    } finally {
        loading.value = false
    }
}

const close = () => {
    open.value = false
    form.resetForm()
}

watch(open, (val) => {
    if (val) init()
})

onMounted(() => {
    if (open.value) {
        init()
    }
})
</script>
